

"use client"

import { useState, useEffect, useRef } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { TabGroup, TabGroupList, TabGroupTrigger } from "@/components/ui/tab-group"
import { Search, Download, Loader2 } from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"
import { DownloadDatepickerDialog } from "@/components/shared/download-datepicker-dialog"
import type { DateRange } from "react-day-picker"

// API Types
interface Transaction {
  id: string
  reference: string
  type: 'package_purchase' | 'credit_topup'
  status: 'created' | 'completed' | 'pending' | 'failed' | 'expired'
  amount: number
  originalAmount: number
  currency: string
  description: string
  creditAmount?: number
  packageId?: string
  bankCode?: string
  discountAmount: number
  createdAt: string
  updatedAt: string
}

interface PaymentHistoryResponse {
  success: boolean
  data: {
    transactions: Transaction[]
    summary: {
      totalSpent: number
      totalTransactions: number
      lastPayment: string
    }
    filters: {
      type: string
      status: string
      dateRange: { start: string; end: string } | null
    }
    pagination: {
      page: number
      limit: number
      total: number
      pages: number
    }
  }
}

export default function AccountHistoryPage() {
  const [activeTab, setActiveTab] = useState<'package' | 'credit'>('package')
  const [searchQuery, setSearchQuery] = useState("")
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const hasFetchedRef = useRef<string>('')
  
  // Data states
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [summary, setSummary] = useState({ totalSpent: 0, totalTransactions: 0, lastPayment: '' })
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch payment history
  const fetchPaymentHistory = async (page: number = 1, type: string = 'all') => {
    try {
      setIsLoading(true)
      setError(null)

      const token = localStorage.getItem('authToken')
      if (!token) {
        setError('ไม่พบ token การยืนยันตัวตน')
        return
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        type: type === 'package' ? 'package_purchase' : type === 'credit' ? 'credit_topup' : 'all',
        status: 'all'
      })

      if (searchQuery) {
        // Search by reference number
        params.append('reference', searchQuery)
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/customer/payment-history?${params}`,
        { headers }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch payment history')
      }

      const result: PaymentHistoryResponse = await response.json()
      
      if (result.success) {
        const sorted = [...result.data.transactions].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        setTransactions(sorted)
        setSummary(result.data.summary)
        setPagination(result.data.pagination)
      }
    } catch (err) {
      console.error('Error fetching payment history:', err)
      setError('ไม่สามารถโหลดประวัติการชำระเงินได้')
    } finally {
      setIsLoading(false)
    }
  }

  // Initial load
  useEffect(() => {
    // Guard against strict mode double invoke - only fetch if activeTab changed
    if (hasFetchedRef.current === activeTab) {
      return
    }
    hasFetchedRef.current = activeTab
    console.log('📋 Fetching payment history for tab:', activeTab)
    fetchPaymentHistory(1, activeTab)
  }, [activeTab])

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount)
  }

  // Handle tab change
  const handleTabChange = (tab: string) => {
    setActiveTab(tab as 'package' | 'credit')
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  // Handle search
  const handleSearch = () => {
    fetchPaymentHistory(1, activeTab)
  }

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    fetchPaymentHistory(newPage, activeTab)
  }

  const handleDownloadConfirm = async (dateRange: DateRange | undefined) => {
    try {
      setIsExporting(true)

      const token = localStorage.getItem('authToken')
      if (!token) {
        alert('ไม่พบ token การยืนยันตัวตน')
        return
      }

      const params = new URLSearchParams({
        type: activeTab === 'package' ? 'package_purchase' : activeTab === 'credit' ? 'credit_topup' : 'all',
        status: 'all'
      })

      if (searchQuery) {
        params.append('reference', searchQuery)
      }

      if (dateRange?.from) {
        params.append('startDate', dateRange.from.toISOString())
      }
      if (dateRange?.to) {
        // Include full end date day by setting time to end-of-day
        const endDate = new Date(dateRange.to)
        endDate.setHours(23, 59, 59, 999)
        params.append('endDate', endDate.toISOString())
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/customer/payment-history/export?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Export failed' }))
        throw new Error(err.message || 'Export failed')
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url

      // Use filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/)
      a.download = filenameMatch?.[1] ?? `payment-history-${new Date().toISOString().slice(0, 10)}.xlsx`

      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

    } catch (err) {
      console.error('Export error:', err)
      alert('ไม่สามารถดาวน์โหลดเอกสารได้ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Title */}
        <h1 className="text-2xl font-bold">ประวัติการใช้งานทั้งหมด</h1>

        {/* Tabs */}
        <TabGroup value={activeTab} onValueChange={handleTabChange} color="brand-blue" fullWidth>
          <TabGroupList>
            <TabGroupTrigger value="package" color="blue">
              ประวัติการซื้อแพ็กเกจ ({activeTab === 'package' ? pagination.total : '-'})
            </TabGroupTrigger>
            <TabGroupTrigger value="credit" color="blue">
              ประวัติการเติมเครดิต ({activeTab === 'credit' ? pagination.total : '-'})
            </TabGroupTrigger>
          </TabGroupList>
        </TabGroup>

        {/* Search and Download Section */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Search Bar */}
          <div className="flex gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="รหัส Invoice"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
                disabled={isLoading}
              />
            </div>
            <Button 
              variant="secondary" 
              className="bg-gray-400 hover:bg-gray-500 text-white"
              onClick={handleSearch}
              disabled={isLoading}
            >
              ค้นหา
            </Button>
          </div>

          {/* Download Button */}
          <Button 
            className="bg-brand-light-red hover:bg-brand-light-red/90 text-white"
            onClick={() => setIsDatePickerOpen(true)}
            disabled={isLoading || isExporting}
          >
            {isExporting
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />
            }
            {isExporting ? 'กำลังดาวน์โหลด...' : 'ดาวน์โหลดเอกสาร'}
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>รหัส Invoice</TableHead>
                <TableHead>รายละเอียด</TableHead>
                <TableHead>จำนวนเงิน</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead>วันที่ทำรายการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-primary" />
                    <p className="mt-2 text-muted-foreground">กำลังโหลดข้อมูล...</p>
                  </TableCell>
                </TableRow>
              ) : transactions.length > 0 ? (
                transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell className="font-medium">{transaction.reference}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{transaction.description}</p>
                        {transaction.creditAmount && (
                          <p className="text-sm text-muted-foreground">
                            {transaction.creditAmount.toLocaleString('th-TH')} เครดิต
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{formatCurrency(transaction.amount)}</p>
                        {transaction.discountAmount > 0 && (
                          <p className="text-sm text-green-600">
                            ส่วนลด {formatCurrency(transaction.discountAmount)}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                        transaction.status === 'pending' || transaction.status === 'created' ? 'bg-yellow-100 text-yellow-800' :
                        transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                        transaction.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {transaction.status === 'completed' ? 'สำเร็จ' :
                         transaction.status === 'pending' || transaction.status === 'created' ? 'รอดำเนินการ' :
                         transaction.status === 'failed' ? 'ล้มเหลว' :
                         transaction.status === 'expired' ? 'หมดอายุ' :
                         'ไม่ระบุ'}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(transaction.createdAt)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    ไม่มีข้อมูล
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-2 py-4 border-t">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              disabled={pagination.page <= 1 || isLoading}
              onClick={() => handlePageChange(1)}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              disabled={pagination.page <= 1 || isLoading}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="icon" className="h-8 w-8 bg-brand-primary hover:bg-brand-primary/90 text-white">
              {pagination.page}
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              disabled={pagination.page >= pagination.pages || isLoading}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              disabled={pagination.page >= pagination.pages || isLoading}
              onClick={() => handlePageChange(pagination.pages)}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Summary */}
        {!isLoading && transactions.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">สรุปยอดรวม</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-blue-700">ยอดใช้จ่ายทั้งหมด</p>
                <p className="text-lg font-bold text-blue-900">{formatCurrency(summary.totalSpent)}</p>
              </div>
              <div>
                <p className="text-sm text-blue-700">จำนวนรายการ</p>
                <p className="text-lg font-bold text-blue-900">{summary.totalTransactions} รายการ</p>
              </div>
              <div>
                <p className="text-sm text-blue-700">รายการล่าสุด</p>
                <p className="text-lg font-bold text-blue-900">
                  {summary.lastPayment ? formatDate(summary.lastPayment) : '-'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Download Date Picker Dialog */}
      <DownloadDatepickerDialog
        open={isDatePickerOpen}
        onOpenChange={setIsDatePickerOpen}
        onConfirm={handleDownloadConfirm}
      />
    </DashboardLayout>
  )
}
